import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../shared/Modal';
import { authService } from '../../../services/auth/authService';
import {
  configService,
  DiscountCode,
  DiscountType,
  DiscountCodeCreate,
} from '../../../services/configService';

const DISCOUNT_TYPE_OPTIONS: { value: DiscountType; label: string }[] = [
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'fixed', label: 'Fixed Amount ($)' },
  { value: 'per_night', label: 'Per Night ($)' },
  { value: 'free_nights', label: 'Free Nights' },
];

interface FormState {
  code: string;
  description: string;
  discount_type: DiscountType;
  discount_value: string;
  min_nights: string;
  min_booking_amount: string;
  max_discount_amount: string;
  max_uses_per_customer: string;
  valid_from: string;
  valid_until: string;
  stay_valid_from: string;
  stay_valid_until: string;
}

const EMPTY_FORM: FormState = {
  code: '',
  description: '',
  discount_type: 'percentage',
  discount_value: '',
  min_nights: '',
  min_booking_amount: '',
  max_discount_amount: '',
  max_uses_per_customer: '',
  valid_from: '',
  valid_until: '',
  stay_valid_from: '',
  stay_valid_until: '',
};

const valueLabel = (t: DiscountType): string => {
  switch (t) {
    case 'percentage': return 'Percentage (%)';
    case 'fixed': return 'Amount ($)';
    case 'per_night': return 'Amount Per Night ($)';
    case 'free_nights': return 'Number of Free Nights';
  }
};

const valuePlaceholder = (t: DiscountType): string => {
  switch (t) {
    case 'percentage': return 'e.g., 20';
    case 'fixed': return 'e.g., 50.00';
    case 'per_night': return 'e.g., 10.00';
    case 'free_nights': return 'e.g., 1';
  }
};

const summarizeValue = (dc: DiscountCode): string => {
  const v = dc.discount_value;
  switch (dc.discount_type) {
    case 'percentage': return `${v}%${dc.max_discount_amount ? ` (max $${dc.max_discount_amount})` : ''}`;
    case 'fixed': return `$${Number(v).toFixed(2)} off`;
    case 'per_night': return `$${Number(v).toFixed(2)}/night`;
    case 'free_nights': return `${v} free night${Number(v) !== 1 ? 's' : ''}`;
    default: return String(v);
  }
};

const DiscountCodesCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DiscountCode | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const storeId = authService.getStoreId();

  const loadCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await configService.getDiscountCodes(storeId || undefined);
      setCodes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (dc: DiscountCode) => {
    setEditing(dc);
    setForm({
      code: dc.code,
      description: dc.description || '',
      discount_type: dc.discount_type,
      discount_value: String(dc.discount_value ?? ''),
      min_nights: dc.min_nights != null ? String(dc.min_nights) : '',
      min_booking_amount: dc.min_booking_amount != null ? String(dc.min_booking_amount) : '',
      max_discount_amount: dc.max_discount_amount != null ? String(dc.max_discount_amount) : '',
      max_uses_per_customer: dc.max_uses_per_customer != null ? String(dc.max_uses_per_customer) : '',
      valid_from: dc.valid_from || '',
      valid_until: dc.valid_until || '',
      stay_valid_from: dc.stay_valid_from || '',
      stay_valid_until: dc.stay_valid_until || '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const setField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): string | null => {
    if (!editing && !form.code.trim()) return 'Code is required';
    const val = parseFloat(form.discount_value);
    if (!form.discount_value || isNaN(val) || val <= 0) return 'Discount value must be greater than 0';
    if (form.discount_type === 'percentage' && val > 100) return 'Percentage cannot exceed 100';
    if (form.discount_type === 'free_nights' && (!Number.isInteger(val) || val < 1)) {
      return 'Free nights must be a whole number (1 or more)';
    }
    if (form.min_nights && (isNaN(parseInt(form.min_nights)) || parseInt(form.min_nights) < 1)) {
      return 'Min nights must be a positive number';
    }
    if (form.max_uses_per_customer && (isNaN(parseInt(form.max_uses_per_customer)) || parseInt(form.max_uses_per_customer) < 1)) {
      return 'Max uses per customer must be a positive number';
    }
    return null;
  };

  const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        // Backend update ignores code / discount_type / store_id
        await configService.updateDiscountCode(editing.id, {
          description: form.description.trim() || undefined,
          discount_value: parseFloat(form.discount_value),
          min_nights: numOrNull(form.min_nights),
          min_booking_amount: numOrNull(form.min_booking_amount),
          max_discount_amount: numOrNull(form.max_discount_amount),
          max_uses_per_customer: numOrNull(form.max_uses_per_customer),
          valid_from: form.valid_from || null,
          valid_until: form.valid_until || null,
          stay_valid_from: form.stay_valid_from || null,
          stay_valid_until: form.stay_valid_until || null,
        });
      } else {
        const payload: DiscountCodeCreate = {
          ...(storeId ? { store_id: storeId } : {}),
          code: form.code.trim().toUpperCase(),
          description: form.description.trim() || undefined,
          discount_type: form.discount_type,
          discount_value: parseFloat(form.discount_value),
          min_nights: numOrNull(form.min_nights),
          min_booking_amount: numOrNull(form.min_booking_amount),
          max_discount_amount: numOrNull(form.max_discount_amount),
          max_uses_per_customer: numOrNull(form.max_uses_per_customer),
          valid_from: form.valid_from || null,
          valid_until: form.valid_until || null,
          stay_valid_from: form.stay_valid_from || null,
          stay_valid_until: form.stay_valid_until || null,
        };
        await configService.createDiscountCode(payload);
      }
      setModalOpen(false);
      await loadCodes();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save discount code');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (dc: DiscountCode) => {
    try {
      await configService.updateDiscountCode(dc.id, { active: !dc.active });
      await loadCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update discount code');
    }
  };

  const handleDelete = async (dc: DiscountCode) => {
    if (!window.confirm(`Delete discount code "${dc.code}"? This cannot be undone.`)) return;
    try {
      await configService.deleteDiscountCode(dc.id);
      await loadCodes();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete discount code');
    }
  };

  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: '0.85rem' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.875rem' };
  const rowStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 };

  return (
    <div className="config-card">
      <div className="config-card-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="config-card-title">
          <span className="config-card-icon">%</span>
          <h2>Discount Codes</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/discount_codes</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            {String.fromCharCode(9660)}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={openAdd}>+ Add Discount Code</button>
          </div>

          {loading ? (
            <div className="loading-state">Loading discount codes...</div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-primary btn-sm" onClick={loadCodes}>Retry</button>
            </div>
          ) : codes.length === 0 ? (
            <div className="empty-state">
              <p>No discount codes yet. Add one to offer promotional pricing at booking.</p>
            </div>
          ) : (
            codes.map((dc) => (
              <div
                key={dc.id}
                className="config-item"
                style={{ marginBottom: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 6, opacity: dc.active ? 1 : 0.6 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong style={{ fontSize: '0.95rem', fontFamily: 'monospace' }}>{dc.code}</strong>
                      <span
                        style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem',
                          background: dc.active ? '#dcfce7' : '#f3f4f6',
                          color: dc.active ? '#166534' : '#6b7280',
                        }}
                      >
                        {dc.active ? 'Active' : 'Inactive'}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{summarizeValue(dc)}</span>
                    </div>
                    {dc.description && (
                      <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>{dc.description}</p>
                    )}
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: '0.75rem', color: '#6b7280' }}>
                      {dc.min_nights ? <span>Min {dc.min_nights} nights</span> : null}
                      {dc.min_booking_amount ? <span>Min ${dc.min_booking_amount}</span> : null}
                      {dc.max_uses_per_customer ? <span>{dc.max_uses_per_customer}/customer</span> : null}
                      {(dc.valid_from || dc.valid_until) ? <span>Book {dc.valid_from || '…'} → {dc.valid_until || '…'}</span> : null}
                      {(dc.stay_valid_from || dc.stay_valid_until) ? <span>Stay {dc.stay_valid_from || '…'} → {dc.stay_valid_until || '…'}</span> : null}
                      <span>Used {dc.usage_count}×</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button className="btn btn-sm" onClick={() => handleToggle(dc)}>{dc.active ? 'Deactivate' : 'Activate'}</button>
                    <button className="btn btn-sm" onClick={() => openEdit(dc)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(dc)}>Delete</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Edit Discount Code — ${editing.code}` : 'Add Discount Code'}
        size="medium"
        footer={
          <>
            <button className="btn" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Code'}
            </button>
          </>
        }
      >
        {formError && (
          <div style={{ marginBottom: 12, padding: 8, background: '#fee2e2', color: '#dc2626', borderRadius: 4, fontSize: '0.85rem' }}>
            {formError}
          </div>
        )}

        <div style={rowStyle}>
          <div>
            <label style={labelStyle}>Code {editing ? '(not editable)' : '*'}</label>
            <input
              style={inputStyle}
              value={form.code}
              disabled={Boolean(editing)}
              placeholder="e.g., SUMMER20"
              onChange={(e) => setField('code', e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <label style={labelStyle}>Discount Type {editing ? '(not editable)' : '*'}</label>
            <select
              style={inputStyle}
              value={form.discount_type}
              disabled={Boolean(editing)}
              onChange={(e) => setField('discount_type', e.target.value)}
            >
              {DISCOUNT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Description</label>
          <input
            style={inputStyle}
            value={form.description}
            placeholder="Internal label, e.g., Summer 2026 promo"
            onChange={(e) => setField('description', e.target.value)}
          />
        </div>

        <div style={rowStyle}>
          <div>
            <label style={labelStyle}>{valueLabel(form.discount_type)} *</label>
            <input
              style={inputStyle}
              type="number"
              step="0.01"
              value={form.discount_value}
              placeholder={valuePlaceholder(form.discount_type)}
              onChange={(e) => setField('discount_value', e.target.value)}
            />
          </div>
          {form.discount_type === 'percentage' && (
            <div>
              <label style={labelStyle}>Max Discount Cap ($)</label>
              <input
                style={inputStyle}
                type="number"
                step="0.01"
                value={form.max_discount_amount}
                placeholder="Optional cap"
                onChange={(e) => setField('max_discount_amount', e.target.value)}
              />
            </div>
          )}
        </div>

        <div style={rowStyle}>
          <div>
            <label style={labelStyle}>Min Nights</label>
            <input style={inputStyle} type="number" value={form.min_nights} placeholder="Optional" onChange={(e) => setField('min_nights', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Min Booking Amount ($)</label>
            <input style={inputStyle} type="number" step="0.01" value={form.min_booking_amount} placeholder="Optional" onChange={(e) => setField('min_booking_amount', e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Max Uses Per Customer</label>
          <input style={inputStyle} type="number" value={form.max_uses_per_customer} placeholder="Blank = unlimited" onChange={(e) => setField('max_uses_per_customer', e.target.value)} />
        </div>

        <div style={rowStyle}>
          <div>
            <label style={labelStyle}>Bookable From</label>
            <input style={inputStyle} type="date" value={form.valid_from} onChange={(e) => setField('valid_from', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Bookable Until</label>
            <input style={inputStyle} type="date" value={form.valid_until} onChange={(e) => setField('valid_until', e.target.value)} />
          </div>
        </div>

        <div style={rowStyle}>
          <div>
            <label style={labelStyle}>Stay Valid From</label>
            <input style={inputStyle} type="date" value={form.stay_valid_from} onChange={(e) => setField('stay_valid_from', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Stay Valid Until</label>
            <input style={inputStyle} type="date" value={form.stay_valid_until} onChange={(e) => setField('stay_valid_until', e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DiscountCodesCard;
